/**
 * 轻量提示（toast）工具 —— 统一 2.5 秒自动消失。
 *
 * 为什么不用 this.$message 直接调：
 *   Element UI 的 Message 默认 3000ms，且各处调用时长不一致，
 *   这里统一成 2500ms，并固定「颜色 + 图标 + 动画」三通道同步反馈
 *   （动效不作为唯一反馈通道）。
 *
 * 用法：
 *   this.$toast.success('已复制')
 *   this.$toast.error('保存失败', '接口异常')
 *   this.$toast.info('正在同步…', 0)   // 0 = 不自动关闭
 */

import { Message } from 'element-ui'

// TOAST_DURATION 是成功/失败反馈的统一停留时长（毫秒）。
export const TOAST_DURATION = 2500

// TYPE_CLASS 把提示类型映射到 global.scss 中定义的配色类。
const TYPE_CLASS = {
  success: 'toast-success',
  error: 'toast-error',
  warning: 'toast-warning',
  info: 'toast-info'
}

/**
 * 弹出提示。
 * @param {string} type 类型：success / error / warning / info。
 * @param {string} message 文案。
 * @param {number} duration 停留时长（毫秒），传 0 表示不自动关闭。
 * @param {boolean} showClose 是否显示手动关闭按钮，长时间提示建议开启。
 * @returns {Object} Element UI Message 实例，可调用 close()。
 */
export function showToast(type, message, duration = TOAST_DURATION, showClose = false) {
  // 文案为空时不弹空提示，避免出现无意义的空条。
  if (!message) return null

  return Message({
    type,
    message,
    duration,
    showClose,
    customClass: TYPE_CLASS[type] || 'toast-info',
    // 提示条自身不使用弹跳动画，只用柔出位移，避免高频提示时晃眼。
    dangerouslyUseHTMLString: false
  })
}

// $toast 是挂到 Vue.prototype 上的便捷入口。
export const $toast = {
  success: (message, duration) => showToast('success', message, duration),
  error: (message, duration) => showToast('error', message, duration),
  warning: (message, duration) => showToast('warning', message, duration),
  info: (message, duration) => showToast('info', message, duration)
}

// ANIMATION_CURVES 汇总动效性格，供 JS 侧（拖拽吸附、弹层动画）复用。
export const ANIMATION_CURVES = {
  // 拖拽/吸附：快进慢收、零回弹，落位时瞬间咬死。
  drag: 'cubic-bezier(0.5, 0, 0.75, 0)',
  // 反馈类：轻微回弹（overshoot），用于点赞、复制成功等正向操作。
  feedback: 'cubic-bezier(0.34, 1.4, 0.64, 1)',
  // 通用柔出：进入动画、面板展开。
  outSoft: 'cubic-bezier(0.25, 0.1, 0.25, 1)'
}

// prefersReducedMotion 返回用户是否偏好减少动态效果，供 JS 动画分支判断。
export function prefersReducedMotion() {
  return typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

export default {
  install(Vue) {
    Vue.prototype.$toast = $toast
    Vue.prototype.$prefersReducedMotion = prefersReducedMotion
  }
}
